import { useState, useEffect, useCallback, useMemo } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Receipt, Plus, Pencil, Trash2, TrendingDown, Calendar, BarChart3, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TransportExpenses() {
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('');

  const [showAddEntry, setShowAddEntry] = useState(false);
  const [showEditEntry, setShowEditEntry] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [newEntry, setNewEntry] = useState({ date: new Date().toISOString().slice(0, 10), category: '', description: '', amount: '' });
  const [customCategory, setCustomCategory] = useState('');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const formatCurrency = (amount) => new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(amount || 0);
  const formatMonthFull = (m) => {
    if (!m) return '';
    const [y, mo] = m.split('-');
    const names = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    return `${names[parseInt(mo)] || mo} ${y}`;
  };

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (selectedMonth && selectedMonth !== 'all') params.month = selectedMonth;
      else if (selectedYear && selectedYear !== 'all') params.year = selectedYear;

      const [entryRes, summaryRes] = await Promise.all([
        axios.get(`${API_URL}/api/transport-expenses/entries`, { headers, params }),
        axios.get(`${API_URL}/api/transport-expenses/summary`, { headers })
      ]);
      setEntries(entryRes.data);
      setSummary(summaryRes.data);
    } catch { toast.error('Failed to load expense data'); }
    finally { setLoading(false); }
  }, [selectedMonth, selectedYear]);

  useEffect(() => { fetchData(); }, [selectedMonth, selectedYear]);
  useEffect(() => { if (summary && !selectedMonth && !selectedYear) setSelectedMonth(summary.current_month); }, [summary]);

  const handleAddEntry = async () => {
    const cat = newEntry.category === '__custom__' ? customCategory : newEntry.category;
    if (!cat || !newEntry.amount) { toast.error('Category and amount required'); return; }
    try {
      await axios.post(`${API_URL}/api/transport-expenses/entries`, { ...newEntry, category: cat }, { headers });
      toast.success('Expense added!');
      setNewEntry({ date: new Date().toISOString().slice(0, 10), category: '', description: '', amount: '' });
      setCustomCategory('');
      setShowAddEntry(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleEditEntry = async () => {
    if (!editingEntry) return;
    const cat = editingEntry.category === '__custom__' ? editingEntry._customCat : editingEntry.category;
    try {
      await axios.put(`${API_URL}/api/transport-expenses/entries/${editingEntry.id}`, {
        date: editingEntry.date, category: cat, description: editingEntry.description,
        amount: parseFloat(editingEntry.amount) || 0
      }, { headers });
      toast.success('Updated!'); setShowEditEntry(false); setEditingEntry(null); fetchData();
    } catch { toast.error('Failed'); }
  };

  const handleDeleteEntry = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    try { await axios.delete(`${API_URL}/api/transport-expenses/entries/${id}`, { headers }); toast.success('Deleted!'); fetchData(); }
    catch { toast.error('Failed'); }
  };

  const filteredTotal = entries.reduce((s, e) => s + (e.amount || 0), 0);
  const categories = summary?.categories || [];

  // Categories that roll-up under the "OPERATIONAL EXPENSES" parent header
  // (case-insensitive match). Update this set to include / exclude a category.
  const OPERATIONAL_CATEGORIES = useMemo(() => new Set([
    'salaries',
    'vehicle finance',
    'insurance',
    'rental vehicles',
    'recovery',
    'maintenance',
    'accountant',
    'tracker',
    'storage',
    'office',
    'routing',
    'website',
    'software',
  ]), []);
  const isOperational = useCallback((cat) => OPERATIONAL_CATEGORIES.has((cat || '').trim().toLowerCase()), [OPERATIONAL_CATEGORIES]);

  // Collapsible state for the parent group
  const [opExpanded, setOpExpanded] = useState(true);

  // Group entries by category for display
  const groupedEntries = useMemo(() => {
    const groups = {};
    entries.forEach(e => {
      const cat = e.category || 'General';
      if (!groups[cat]) groups[cat] = { entries: [], total: 0 };
      groups[cat].entries.push(e);
      groups[cat].total += e.amount || 0;
    });
    return Object.entries(groups).sort((a, b) => b[1].total - a[1].total);
  }, [entries]);

  // Split grouped entries: operational rolls under parent, rest stays flat
  const operationalGroups = useMemo(() => groupedEntries.filter(([cat]) => isOperational(cat)), [groupedEntries, isOperational]);
  const nonOperationalGroups = useMemo(() => groupedEntries.filter(([cat]) => !isOperational(cat)), [groupedEntries, isOperational]);
  const operationalTotal = useMemo(() => operationalGroups.reduce((s, [, g]) => s + g.total, 0), [operationalGroups]);

  // Category chart data
  const byCategoryData = useMemo(() => {
    if (!summary?.by_category) return [];
    return Object.entries(summary.by_category).map(([cat, total]) => ({ category: cat, total }));
  }, [summary]);
  const maxCat = Math.max(...byCategoryData.map(d => d.total), 1);
  const opCategoryData = useMemo(() => byCategoryData.filter(d => isOperational(d.category)).sort((a, b) => b.total - a.total), [byCategoryData, isOperational]);
  const nonOpCategoryData = useMemo(() => byCategoryData.filter(d => !isOperational(d.category)).sort((a, b) => b.total - a.total), [byCategoryData, isOperational]);
  const opCategoryTotal = useMemo(() => opCategoryData.reduce((s, d) => s + d.total, 0), [opCategoryData]);
  const [opByCatExpanded, setOpByCatExpanded] = useState(true);

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64 text-slate-600">Loading...</div></DashboardLayout>;

  const renderCategoryCard = (cat, group) => (
    <Card key={cat} className="bg-white border-slate-200 shadow-sm">
      <CardHeader className="py-3 px-4">
        <CardTitle className="text-slate-900 text-sm flex justify-between items-center">
          <span className="flex items-center gap-2">
            <span className="bg-rose-100 text-rose-700 px-2 py-0.5 rounded text-xs font-bold">{cat}</span>
            <span className="text-slate-600 text-xs">({group.entries.length} items)</span>
          </span>
          <span className="text-rose-700 font-bold">{formatCurrency(group.total)}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableBody>
            {group.entries.map(e => (
              <TableRow key={e.id} className="border-slate-200">
                <TableCell className="text-slate-700 text-sm w-24">{new Date(e.date).toLocaleDateString('en-ZA')}</TableCell>
                <TableCell className="text-slate-900 text-sm">{e.description}</TableCell>
                <TableCell className="text-rose-700 text-right font-bold w-32">{formatCurrency(e.amount)}</TableCell>
                <TableCell className="text-right w-20">
                  <div className="flex gap-1 justify-end">
                    <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white h-6 w-6 p-0" onClick={() => { setEditingEntry({...e}); setShowEditEntry(true); }}><Pencil className="h-3 w-3" /></Button>
                    <Button size="sm" className="bg-rose-600 hover:bg-rose-700 text-white h-6 w-6 p-0" onClick={() => handleDeleteEntry(e.id)}><Trash2 className="h-3 w-3" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );

  return (
    <DashboardLayout>
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Receipt className="h-6 w-6 text-rose-700" /> JSM Transport Expenses
            </h1>
            <p className="text-slate-600 text-sm">Monthly Operating Expenses</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              className="bg-violet-700 hover:bg-violet-800 text-white border-violet-600"
              data-testid="seed-jsm-operating-btn"
              onClick={async () => {
                const m = selectedMonth && selectedMonth !== 'all' ? selectedMonth : (summary?.current_month || new Date().toISOString().slice(0,7));
                if (!window.confirm(`Add JSM Operating Expense bundle (15 items) to ${m}? Re-runs skip duplicates.`)) return;
                try {
                  const r = await axios.post(`${API_URL}/api/transport-expenses/seed-jsm-operating`, null, { headers, params: { month: m } });
                  toast.success(`Added ${r.data.inserted} items · ${r.data.skipped_existing} already present`);
                  fetchData();
                } catch (e) { toast.error(e.response?.data?.detail || 'Seed failed'); }
              }}
            >+ JSM Operating Bundle</Button>
            <Button
              variant="outline"
              className="bg-emerald-700 hover:bg-emerald-800 text-white border-emerald-600"
              data-testid="sync-monthly-totals-btn"
              onClick={async () => {
                const m = selectedMonth && selectedMonth !== 'all' ? selectedMonth : (summary?.current_month || new Date().toISOString().slice(0,7));
                try {
                  const r = await axios.post(`${API_URL}/api/transport-expenses/sync-monthly-totals`, null, { headers, params: { month: m } });
                  const inj = r.data.spares_injection_total || 0;
                  toast.success(
                    `Synced ${m}: Salaries R${r.data.payslip_total.toFixed(2)} · Fuel R${r.data.fuel_total.toFixed(2)}` +
                    (inj > 0 ? ` (cash R${r.data.fuel_cash_total.toFixed(2)} + spares injections R${inj.toFixed(2)})` : '')
                  );
                  fetchData();
                } catch (e) { toast.error(e.response?.data?.detail || 'Sync failed'); }
              }}
              title="Refresh Payslips Total + Fuel (Actual) for the selected month"
            >↻ Sync Payslips + Fuel</Button>
            <Dialog open={showAddEntry} onOpenChange={setShowAddEntry}>
            <DialogTrigger asChild>
              <Button className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="add-expense-btn"><Plus className="h-4 w-4 mr-1" /> Add Expense</Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md">
              <DialogHeader><DialogTitle className="flex items-center gap-2"><Receipt className="h-5 w-5 text-rose-700" /> ADD EXPENSE</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div><Label>Date</Label><Input type="date" value={newEntry.date} onChange={e => setNewEntry({...newEntry, date: e.target.value})} className="bg-white border-slate-200" data-testid="exp-date-input" /></div>
                <div>
                  <Label>Category *</Label>
                  <Select value={newEntry.category} onValueChange={v => setNewEntry({...newEntry, category: v})}>
                    <SelectTrigger className="bg-white border-slate-200" data-testid="exp-cat-select"><SelectValue placeholder="Select or add new..." /></SelectTrigger>
                    <SelectContent className="bg-white border-slate-200 max-h-60">
                      {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      <SelectItem value="__custom__">+ New Category</SelectItem>
                    </SelectContent>
                  </Select>
                  {newEntry.category === '__custom__' && (
                    <Input value={customCategory} onChange={e => setCustomCategory(e.target.value.toUpperCase())} placeholder="Type new category name" className="bg-white border-slate-200 mt-2 uppercase" data-testid="exp-custom-cat" />
                  )}
                </div>
                <div><Label>Description *</Label><Input value={newEntry.description} onChange={e => setNewEntry({...newEntry, description: e.target.value})} placeholder="e.g., Demorent, Hollard" className="bg-white border-slate-200" data-testid="exp-desc-input" /></div>
                <div><Label>Amount (R) *</Label><Input type="number" step="0.01" value={newEntry.amount} onChange={e => setNewEntry({...newEntry, amount: e.target.value})} placeholder="0.00" className="bg-white border-slate-200" data-testid="exp-amount-input" /></div>
                <Button onClick={handleAddEntry} className="w-full bg-rose-600 hover:bg-rose-700" data-testid="save-expense-btn">SAVE EXPENSE</Button>
              </div>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="bg-gradient-to-r from-rose-500 to-rose-600 border-0 text-white">
            <CardContent className="p-3">
              <div className="flex items-center gap-1 mb-1"><Calendar className="h-4 w-4" /><span className="text-xs opacity-80">Month to Date</span></div>
              <p className="text-xl font-bold" data-testid="exp-mtd-total">{formatCurrency(summary?.mtd_total)}</p>
              <p className="text-xs opacity-70">{summary?.mtd_count || 0} items</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-r from-orange-500 to-orange-600 border-0 text-white">
            <CardContent className="p-3">
              <div className="flex items-center gap-1 mb-1"><TrendingDown className="h-4 w-4" /><span className="text-xs opacity-80">Year to Date</span></div>
              <p className="text-xl font-bold" data-testid="exp-ytd-total">{formatCurrency(summary?.ytd_total)}</p>
              <p className="text-xs opacity-70">{summary?.ytd_count || 0} items</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-r from-amber-500 to-amber-600 border-0 text-white">
            <CardContent className="p-3">
              <div className="flex items-center gap-1 mb-1"><BarChart3 className="h-4 w-4" /><span className="text-xs opacity-80">Current View</span></div>
              <p className="text-xl font-bold">{formatCurrency(filteredTotal)}</p>
              <p className="text-xs opacity-70">{entries.length} items</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-r from-violet-500 to-violet-600 border-0 text-white">
            <CardContent className="p-3">
              <div className="flex items-center gap-1 mb-1"><Calendar className="h-4 w-4" /><span className="text-xs opacity-80">Period</span></div>
              <p className="text-lg font-bold">{selectedMonth && selectedMonth !== 'all' ? formatMonthFull(selectedMonth) : 'All Time'}</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="entries">
          <TabsList className="bg-slate-100 mb-4">
            <TabsTrigger value="entries" className="data-[state=active]:bg-rose-500 data-[state=active]:text-white text-slate-700" data-testid="exp-entries-tab">Expense Items</TabsTrigger>
            <TabsTrigger value="by-category" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white text-slate-700" data-testid="exp-category-tab">By Category</TabsTrigger>
          </TabsList>

          {/* Entries Tab */}
          <TabsContent value="entries">
            <div className="flex flex-wrap items-end gap-3 mb-4">
              <div className="min-w-[180px]">
                <Label className="text-slate-600 text-xs">Month</Label>
                <Select value={selectedMonth} onValueChange={v => { setSelectedMonth(v); setSelectedYear(''); }}>
                  <SelectTrigger className="bg-white border-slate-300 text-slate-900" data-testid="exp-month-filter"><SelectValue placeholder="Month" /></SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    <SelectItem value="all">All Months</SelectItem>
                    {(summary?.available_months || []).map(m => <SelectItem key={m} value={m}>{formatMonthFull(m)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="min-w-[120px]">
                <Label className="text-slate-600 text-xs">Year</Label>
                <Select value={selectedYear} onValueChange={v => { setSelectedYear(v); setSelectedMonth(''); }}>
                  <SelectTrigger className="bg-white border-slate-300 text-slate-900"><SelectValue placeholder="Year" /></SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    <SelectItem value="all">All Years</SelectItem>
                    {(summary?.available_years || []).map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Grouped by category */}
            {groupedEntries.length === 0 ? (
              <Card className="bg-white border-slate-200 shadow-sm"><CardContent className="p-6"><p className="text-slate-600 text-center">No expenses for this period.</p></CardContent></Card>
            ) : (
              <div className="space-y-4">
                {/* OPERATIONAL EXPENSES collapsible parent */}
                {operationalGroups.length > 0 && (
                  <Card className="bg-slate-50 border-slate-300 shadow-sm" data-testid="op-expenses-parent">
                    <CardHeader
                      className="py-3 px-4 cursor-pointer select-none hover:bg-slate-100 rounded-t-lg"
                      onClick={() => setOpExpanded(v => !v)}
                      data-testid="op-expenses-toggle"
                    >
                      <CardTitle className="text-slate-900 text-base flex justify-between items-center">
                        <span className="flex items-center gap-2">
                          {opExpanded ? <ChevronDown className="h-5 w-5 text-slate-700" /> : <ChevronRight className="h-5 w-5 text-slate-700" />}
                          <span className="bg-slate-900 text-white px-2.5 py-1 rounded text-xs font-bold tracking-wide">OPERATIONAL EXPENSES</span>
                          <span className="text-slate-600 text-xs">({operationalGroups.length} categories · {operationalGroups.reduce((s, [, g]) => s + g.entries.length, 0)} items)</span>
                        </span>
                        <span className="text-slate-900 font-black text-lg" data-testid="op-expenses-total">{formatCurrency(operationalTotal)}</span>
                      </CardTitle>
                    </CardHeader>
                    {opExpanded && (
                      <CardContent className="p-3 pt-0 space-y-3">
                        {operationalGroups.map(([cat, group]) => renderCategoryCard(cat, group))}
                      </CardContent>
                    )}
                  </Card>
                )}
                {/* Non-operational categories at the top level */}
                {nonOperationalGroups.map(([cat, group]) => renderCategoryCard(cat, group))}
                <Card className="bg-slate-50 border-slate-200">
                  <CardContent className="p-4 flex justify-between items-center">
                    <span className="text-slate-900 font-bold text-lg">TOTAL MONTHLY EXPENSES ({entries.length} items)</span>
                    <span className="text-rose-700 font-black text-2xl" data-testid="exp-total">{formatCurrency(filteredTotal)}</span>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* By Category Tab */}
          <TabsContent value="by-category">
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader><CardTitle className="text-slate-900 text-base">Expenses by Category — {selectedMonth && selectedMonth !== 'all' ? formatMonthFull(selectedMonth) : summary?.current_month ? formatMonthFull(summary.current_month) : 'Current Month'}</CardTitle></CardHeader>
              <CardContent>
                {byCategoryData.length === 0 ? <p className="text-slate-600 text-center py-8">No data.</p> : (
                  <div className="space-y-3">
                    {/* OPERATIONAL EXPENSES parent header */}
                    {opCategoryData.length > 0 && (
                      <div className="border border-slate-300 rounded-lg bg-slate-50" data-testid="op-bycat-parent">
                        <button
                          type="button"
                          onClick={() => setOpByCatExpanded(v => !v)}
                          className="w-full flex items-center justify-between gap-3 px-3 py-2.5 hover:bg-slate-100 rounded-t-lg select-none"
                          data-testid="op-bycat-toggle"
                        >
                          <span className="flex items-center gap-2">
                            {opByCatExpanded ? <ChevronDown className="h-4 w-4 text-slate-700" /> : <ChevronRight className="h-4 w-4 text-slate-700" />}
                            <span className="bg-slate-900 text-white px-2.5 py-1 rounded text-xs font-bold tracking-wide">OPERATIONAL EXPENSES</span>
                            <span className="text-slate-600 text-xs">({opCategoryData.length} categories)</span>
                          </span>
                          <span className="text-slate-900 font-black text-base" data-testid="op-bycat-total">{formatCurrency(opCategoryTotal)}</span>
                        </button>
                        {opByCatExpanded && (
                          <div className="space-y-2 px-3 pb-3 pt-1">
                            {opCategoryData.map(d => {
                              const pct = maxCat > 0 ? (d.total / maxCat) * 100 : 0;
                              return (
                                <div key={d.category} className="flex items-center gap-3">
                                  <div className="w-36 shrink-0 text-right">
                                    <p className="text-slate-900 font-medium text-sm truncate">{d.category}</p>
                                  </div>
                                  <div className="flex-1 bg-white border border-slate-200 rounded-full h-8 overflow-hidden relative">
                                    <div className="h-full rounded-full bg-gradient-to-r from-rose-500 to-rose-600 transition-all duration-500" style={{ width: `${Math.max(pct, 3)}%` }} />
                                    <span className="absolute inset-0 flex items-center px-3 text-sm font-bold text-slate-900">{formatCurrency(d.total)}</span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                    {/* Non-operational categories */}
                    {nonOpCategoryData.map(d => {
                      const pct = maxCat > 0 ? (d.total / maxCat) * 100 : 0;
                      return (
                        <div key={d.category} className="flex items-center gap-3">
                          <div className="w-36 shrink-0 text-right">
                            <p className="text-slate-900 font-medium text-sm truncate">{d.category}</p>
                          </div>
                          <div className="flex-1 bg-slate-100 rounded-full h-8 overflow-hidden relative">
                            <div className="h-full rounded-full bg-gradient-to-r from-rose-500 to-rose-600 transition-all duration-500" style={{ width: `${Math.max(pct, 3)}%` }} />
                            <span className="absolute inset-0 flex items-center px-3 text-sm font-bold text-slate-900">{formatCurrency(d.total)}</span>
                          </div>
                        </div>
                      );
                    })}
                    <div className="mt-4 pt-3 border-t border-slate-200 flex justify-between items-center">
                      <span className="text-slate-900 font-bold">Total</span>
                      <span className="text-rose-700 font-bold text-xl">{formatCurrency(summary?.mtd_total)}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Edit Dialog */}
        <Dialog open={showEditEntry} onOpenChange={setShowEditEntry}>
          <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><Pencil className="h-5 w-5" /> EDIT EXPENSE</DialogTitle></DialogHeader>
            {editingEntry && (
              <div className="space-y-4">
                <div><Label>Date</Label><Input type="date" value={editingEntry.date} onChange={e => setEditingEntry({...editingEntry, date: e.target.value})} className="bg-white border-slate-200" /></div>
                <div>
                  <Label>Category</Label>
                  <Select value={categories.includes(editingEntry.category) ? editingEntry.category : '__custom__'} onValueChange={v => {
                    if (v === '__custom__') { setEditingEntry({...editingEntry, category: '__custom__', _customCat: editingEntry.category}); }
                    else { setEditingEntry({...editingEntry, category: v}); }
                  }}>
                    <SelectTrigger className="bg-white border-slate-200"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-slate-200 max-h-60">
                      {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      <SelectItem value="__custom__">+ New/Rename Category</SelectItem>
                    </SelectContent>
                  </Select>
                  {(editingEntry.category === '__custom__') && (
                    <Input value={editingEntry._customCat || ''} onChange={e => setEditingEntry({...editingEntry, _customCat: e.target.value.toUpperCase()})} placeholder="Type category name" className="bg-white border-slate-200 mt-2 uppercase" />
                  )}
                </div>
                <div><Label>Description</Label><Input value={editingEntry.description || ''} onChange={e => setEditingEntry({...editingEntry, description: e.target.value})} className="bg-white border-slate-200" /></div>
                <div><Label>Amount (R)</Label><Input type="number" step="0.01" value={editingEntry.amount} onChange={e => setEditingEntry({...editingEntry, amount: e.target.value})} className="bg-white border-slate-200" /></div>
                <Button onClick={handleEditEntry} className="w-full bg-blue-600 hover:bg-blue-700">UPDATE EXPENSE</Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
